// src/views/auth/ForgotPassword.js
// --- UPDATE (Forgot / reset password) -----------------------------------------
// /auth/forgot-password — "Forgot password?" from the sign-in page.
//   • Email or username → POST /auth/forgot-password { identifier, channel: 'web' }.
//   • Always the same confirmation ("if an account matches…") — the page never says whether an
//     account exists. Resend is offered after 60 s; rate limits (429) are shown with the wait time.
//   • Pre-fills ?identifier= (the sign-in page passes what the user typed).
// --- END UPDATE ---------------------------------------------------------------
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Box, Button, FormControl, FormLabel, FormErrorMessage, Heading, Icon, Input, InputGroup, InputLeftElement,
    Text, VStack, HStack, Alert, AlertIcon, AlertDescription, useColorModeValue, List, ListItem, ListIcon,
} from '@chakra-ui/react';
import { MdPersonOutline, MdArrowBack, MdMarkEmailRead, MdSend, MdCheckCircle } from 'react-icons/md';
import { AuthPageLayout, authApi } from './passwordResetShared';

const RESEND_AFTER_S = 60;

export default function ForgotPassword() {
    const textColor = useColorModeValue('gray.800', 'white');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const brand = useColorModeValue('blue.600', 'blue.300');
    const inputBg = useColorModeValue('gray.50', 'gray.800');
    const inputFocusBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.600');
    const iconMuted = useColorModeValue('gray.400', 'gray.500');
    const doneBg = useColorModeValue('green.50', 'rgba(72,187,120,0.12)');

    const location = useLocation();
    const [identifier, setIdentifier] = useState(() => {
        try { return new URLSearchParams(location.search).get('identifier') || ''; } catch { return ''; }
    });
    const [error, setError] = useState(null);
    const [fieldError, setFieldError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(null); // { message, to }
    const [cooldown, setCooldown] = useState(0);
    const abortRef = useRef(null);

    useEffect(() => () => abortRef.current?.abort(), []);
    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const submit = useCallback(async (e) => {
        e?.preventDefault?.();
        const id = identifier.trim();
        if (!id) { setFieldError('Enter the email or username of your FlowSense account.'); return; }
        if (loading || cooldown > 0) return;
        setLoading(true); setError(null); setFieldError(null);
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        try {
            const res = await authApi('/forgot-password', { method: 'POST', body: { identifier: id, channel: 'web' }, signal: abortRef.current.signal });
            setSent({ message: res.message, to: id });
            setCooldown(RESEND_AFTER_S);
        } catch (err) {
            if (err.name === 'AbortError') return;
            if (err.status === 429) {
                setError(err.message || 'Too many requests. Try again later.');
                if (err.retryAfter) setCooldown(Math.min(err.retryAfter, 3600));
            } else if (err.status === 400) {
                setFieldError(err.message);
            } else {
                setError('FlowSense could not be reached. Check your connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [identifier, loading, cooldown]);

    return (
        <AuthPageLayout>
            {!sent ? (
                <>
                    <VStack spacing={1} align="flex-start" mb={6}>
                        <Heading color={textColor} fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800">Forgot your password?</Heading>
                        <Text fontSize="md" color={muted}>Enter your email or username and we will email you a link to choose a new one.</Text>
                    </VStack>
                    {error && (
                        <Alert status="error" borderRadius="lg" mb={4} role="alert">
                            <AlertIcon />
                            <AlertDescription fontSize="sm">{error}{cooldown > 0 ? ` (${cooldown}s)` : ''}</AlertDescription>
                        </Alert>
                    )}
                    <Box as="form" onSubmit={submit} noValidate aria-label="Forgot password form">
                        <VStack spacing={5} align="stretch">
                            <FormControl isRequired isInvalid={!!fieldError}>
                                <FormLabel fontSize="sm" fontWeight="semibold" color={textColor} htmlFor="identifier">Email or username</FormLabel>
                                <InputGroup size="lg">
                                    <InputLeftElement pointerEvents="none"><Icon as={MdPersonOutline} color={iconMuted} /></InputLeftElement>
                                    <Input id="identifier" name="identifier" type="text" placeholder="you@company.com or username" borderRadius="xl"
                                        value={identifier} onChange={(e) => { setIdentifier(e.target.value); setFieldError(null); }}
                                        bg={inputBg} _focus={{ bg: inputFocusBg }} borderColor={fieldError ? 'red.500' : border}
                                        autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoFocus isDisabled={loading} />
                                </InputGroup>
                                <FormErrorMessage>{fieldError}</FormErrorMessage>
                            </FormControl>
                            <Button type="submit" w="100%" h="52px" borderRadius="xl" fontWeight="bold" color="white"
                                bgGradient="linear(to-r, blue.500, purple.600)" _hover={{ bgGradient: 'linear(to-r, blue.600, purple.700)', boxShadow: 'lg' }}
                                isLoading={loading} loadingText="Sending…" isDisabled={cooldown > 0} leftIcon={!loading ? <Icon as={MdSend} /> : undefined}>
                                {cooldown > 0 ? `Try again in ${cooldown}s` : 'Email me a reset link'}
                            </Button>
                        </VStack>
                    </Box>
                </>
            ) : (
                <Box data-testid="forgot-sent">
                    <HStack spacing={3} mb={4}>
                        <Icon as={MdMarkEmailRead} boxSize={8} color="green.400" />
                        <Heading color={textColor} fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800">Check your email</Heading>
                    </HStack>
                    <Box p={4} borderRadius="xl" bg={doneBg} mb={5}>
                        <Text fontSize="sm" color={textColor}>{sent.message}</Text>
                    </Box>
                    <List spacing={2} fontSize="sm" color={muted} mb={6}>
                        <ListItem><ListIcon as={MdCheckCircle} color="green.400" />Open the email from FlowSense MOW+ and tap <b>Choose a new password</b>.</ListItem>
                        <ListItem><ListIcon as={MdCheckCircle} color="green.400" />The link works once and expires in 30 minutes.</ListItem>
                        <ListItem><ListIcon as={MdCheckCircle} color="green.400" />No email after a few minutes? Check spam, or ask your administrator to confirm the email on your account.</ListItem>
                    </List>
                    <Button w="100%" variant="outline" borderRadius="xl" onClick={() => submit()} isDisabled={cooldown > 0} isLoading={loading} loadingText="Sending…">
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend the email'}
                    </Button>
                    <Button w="100%" variant="ghost" mt={2} borderRadius="xl" onClick={() => { setSent(null); setError(null); }}>Use a different email or username</Button>
                </Box>
            )}
            <HStack mt={6} justify="center">
                <Text as={NavLink} to="/auth/sign-in" fontSize="sm" color={brand} fontWeight="semibold" display="inline-flex" alignItems="center" gap={1} _hover={{ textDecoration: 'underline' }}>
                    <Icon as={MdArrowBack} /> Back to sign in
                </Text>
            </HStack>
        </AuthPageLayout>
    );
}
